import { create } from 'zustand';

import type { Page, PageType } from '@/types/page';
import type { PaginationInfo } from '@/types/pagination';

export interface ActivePageTypeView {
  pageId: string;
  pageTitle: string;
  pageType: PageType;
  indexPage?: Page;
  children: Page[];
  isIndexPage: boolean;
  pagination?: PaginationInfo;
  searchQuery?: string;
  isLoadingInitial?: boolean;
}

interface NestedPageViewStore {
  activeNestedView: ActivePageTypeView | null;

  setActiveNestedView: (view: ActivePageTypeView | null) => void;
  addPage: (page: Page, indexPageType: PageType) => void;
  updatePage: (page: Partial<Page> & { id: string }) => void;
  removePage: (pageId: string) => void;
  reorderActiveNestedChildren: (orderedIds: string[]) => void;
  appendPages: (pages: Page[], pagination?: PaginationInfo) => void;
  clear: () => void;
}

export const useNestedPageViewStore = create<NestedPageViewStore>((set, get) => {
  const updateView = (updater: (view: ActivePageTypeView) => Partial<ActivePageTypeView>) => {
    const { activeNestedView } = get();
    if (!activeNestedView) return;
    set({ activeNestedView: { ...activeNestedView, ...updater(activeNestedView) } });
  };

  const withPaginationDelta = (
    view: ActivePageTypeView,
    delta: number,
  ): Pick<ActivePageTypeView, 'pagination'> => ({
    pagination: view.pagination
      ? { ...view.pagination, totalCount: view.pagination.totalCount + delta }
      : undefined,
  });

  return {
    activeNestedView: null,

    setActiveNestedView: (view) => {
      set({ activeNestedView: view });
    },

    addPage: (page, indexPageType) => {
      const { activeNestedView } = get();
      if (!activeNestedView?.isIndexPage || activeNestedView.pageType !== indexPageType) return;
      if (activeNestedView.children.some((child) => child.id === page.id)) return;

      updateView((view) => {
        // Insert after pinned pages so new (unpinned) pages don't appear above them
        let insertIndex = 0;
        for (let i = view.children.length - 1; i >= 0; i--) {
          if (view.children[i].pinned) {
            insertIndex = i + 1;
            break;
          }
        }
        const children = [...view.children];
        children.splice(insertIndex, 0, page);
        return { children, ...withPaginationDelta(view, 1) };
      });
    },

    updatePage: (updates) => {
      const { activeNestedView } = get();
      if (!activeNestedView) return;

      const isIndexPage =
        activeNestedView.indexPage?.id === updates.id || activeNestedView.pageId === updates.id;
      const pageIndex = activeNestedView.children.findIndex((child) => child.id === updates.id);
      if (!isIndexPage && pageIndex === -1) return;

      updateView((view) => {
        const nextView: Partial<ActivePageTypeView> = {};

        if (isIndexPage && view.indexPage) {
          nextView.indexPage = { ...view.indexPage, ...updates };
        }

        if (pageIndex !== -1) {
          const updatedChildren = [...view.children];
          updatedChildren[pageIndex] = { ...updatedChildren[pageIndex], ...updates };
          nextView.children = updatedChildren;
        }

        return nextView;
      });
    },

    removePage: (pageId) => {
      const { activeNestedView } = get();
      if (!activeNestedView) return;

      const isDeletedIndex =
        activeNestedView.indexPage?.id === pageId ||
        (activeNestedView.isIndexPage && activeNestedView.pageId === pageId);

      if (isDeletedIndex) {
        updateView(() => ({ indexPage: undefined }));
        return;
      }

      const filteredChildren = activeNestedView.children.filter((child) => child.id !== pageId);
      if (filteredChildren.length === activeNestedView.children.length) return;

      updateView((view) => ({
        children: filteredChildren,
        ...withPaginationDelta(view, -1),
      }));
    },

    reorderActiveNestedChildren: (orderedIds) => {
      const { activeNestedView } = get();
      if (!activeNestedView) return;

      const childrenMap = new Map(activeNestedView.children.map((child) => [child.id, child]));
      const reordered = orderedIds
        .map((id) => childrenMap.get(id))
        .filter((child): child is Page => child !== undefined);

      updateView(() => ({ children: reordered }));
    },

    appendPages: (pages, pagination) => {
      updateView(() => ({
        children: [...(get().activeNestedView?.children || []), ...pages],
        pagination,
      }));
    },

    clear: () => {
      set({ activeNestedView: null });
    },
  };
});
